/**
 * KAN-758: календарь-месяц для выбора периода CRM (без сторонних библиотек).
 * Подсвечивает выбранный период или начатый выбор (draftStart). Будущие дни и
 * дни дальше лимита периода от начатого выбора недоступны.
 * Сетка месяца и стрелки общие: components/calendar/MonthCalendar (KAN-964).
 */
import { MonthCalendar } from '@/components/calendar/MonthCalendar';
import {
  MAX_CRM_RANGE_DAYS,
  compareMonth,
  formatDayFull,
  monthOf,
  rangeLength,
  type MonthRef,
} from '@/lib/crmRange';
import type { CrmRange } from '@/lib/crmTable';

interface CrmRangeCalendarProps {
  month: MonthRef;
  onMonthChange: (month: MonthRef) => void;
  /** null - период не выбран ("Всё время"): дни не подсвечены. */
  range: CrmRange | null;
  draftStart: string | null;
  today: string;
  onPick: (day: string) => void;
}

function isDisabled(day: string, today: string, draftStart: string | null): boolean {
  if (day > today) return true;
  if (!draftStart) return false;
  const [a, b] = day < draftStart ? [day, draftStart] : [draftStart, day];
  return rangeLength(a, b) > MAX_CRM_RANGE_DAYS;
}

function dayClass(day: string, range: CrmRange | null, draftStart: string | null, disabled: boolean): string {
  const base = 'h-8 w-8 rounded-md text-sm tabular-nums';
  if (disabled) return `${base} cursor-not-allowed text-gray-300`;
  if (draftStart) {
    return day === draftStart ? `${base} bg-blue-600 text-white` : `${base} text-gray-800 hover:bg-gray-100`;
  }
  if (!range) return `${base} text-gray-800 hover:bg-gray-100`;
  if (day === range.from || day === range.to) return `${base} bg-blue-600 text-white`;
  if (day > range.from && day < range.to) return `${base} bg-blue-100 text-blue-900`;
  return `${base} text-gray-800 hover:bg-gray-100`;
}

export function CrmRangeCalendar({ month, onMonthChange, range, draftStart, today, onPick }: CrmRangeCalendarProps) {
  return (
    <MonthCalendar
      month={month}
      onMonthChange={onMonthChange}
      canGoNext={compareMonth(month, monthOf(today)) < 0}
      testId="crm-range-calendar"
      monthTestId="crm-range-month"
      renderDay={(day) => {
        const disabled = isDisabled(day, today, draftStart);
        return (
          <button
            type="button"
            aria-label={formatDayFull(day)}
            disabled={disabled}
            className={dayClass(day, range, draftStart, disabled)}
            onClick={() => onPick(day)}
          >
            {Number(day.slice(8))}
          </button>
        );
      }}
    />
  );
}
